import {
  X,
  Loader2,
  AlertCircle,
  UserCircle,
  AtSign,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Shield,
  Clock,
  Stethoscope,
  CheckCircle,
  XCircle,
  KeyRound,
  Pencil,
  Lock,
} from 'lucide-react';
import { useState } from 'react';
import { useUser } from '../../services/httpServices/queries';
import { cn } from '../../lib/utils';
import { ChangeUsernameModal } from './ChangeUsernameModal';
import { ChangePasswordModal } from './ChangePasswordModal';

function getRoleLabel(role: string) {
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getUserTypeInfo(user: any) {
  if (user?.employee?.type === 'doctor') {
    return { label: 'Doctor', icon: Stethoscope, color: 'text-primary bg-primary/10' };
  }
  return { label: 'Employee', icon: Briefcase, color: 'text-secondary bg-secondary/10' };
}

function InfoTile({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border min-w-0">
      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
        <Icon size={16} className="text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-text-muted">{label}</p>
        <div className="text-sm font-medium text-text break-words">{value}</div>
      </div>
    </div>
  );
}

interface UserDetailsModalProps {
  userId: string | null;
  onClose: () => void;
  onEdit?: (user: any) => void;
  onManageRoles?: (user: any) => void;
  /** When true, show the inline change-username / change-password actions. */
  canUpdate?: boolean;
}

/**
 * Read-only user details popup. Opening a row from the Users table shows the
 * profile here instead of navigating to the standalone `/users/:id` page.
 * Clicking the backdrop does not dismiss the dialog.
 */
export function UserDetailsModal({ userId, onClose, onEdit, onManageRoles, canUpdate }: UserDetailsModalProps) {
  const { data, isLoading, error } = useUser(userId || '');
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const user = data?.data;

  if (!userId) return null;

  const typeInfo = user ? getUserTypeInfo(user) : null;
  const TypeIcon = typeInfo?.icon;
  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}` || user?.username?.[0] || '?';

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <UserCircle size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text truncate">
                {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username || 'User details'}
              </h2>
              {user && <p className="text-xs text-text-muted mt-0.5">@{user.username}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-5 overflow-y-auto">
          {isLoading && !user ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : error || !user ? (
            <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
              <AlertCircle size={18} />
              <span>Failed to load user details. Please try again.</span>
            </div>
          ) : (
            <>
              {/* Identity */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xl shrink-0">
                  {initials.toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-text truncate">
                    {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.username}
                  </p>
                  {TypeIcon && typeInfo && (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium',
                        typeInfo.color,
                      )}
                    >
                      <TypeIcon size={12} />
                      {typeInfo.label}
                    </span>
                  )}
                </div>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium shrink-0 ml-auto',
                    user.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700',
                  )}
                >
                  {user.isActive ? <CheckCircle size={12} /> : <XCircle size={12} />}
                  {user.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Core details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InfoTile icon={AtSign} label="Username" value={user.username} />
                <InfoTile icon={Mail} label="Email" value={user.email || 'N/A'} />
                <InfoTile icon={Phone} label="Mobile" value={user.mobile || 'N/A'} />
                <InfoTile icon={Building2} label="Department" value={user.employee?.department?.name || 'N/A'} />
                <InfoTile icon={Briefcase} label="Designation" value={user.employee?.designation?.name || 'N/A'} />
                <InfoTile
                  icon={Clock}
                  label="Created"
                  value={user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                />
              </div>

              {/* System roles */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                  <Shield size={13} />
                  System Roles
                </div>
                <div className="flex flex-wrap gap-1">
                  {user.roles?.length ? (
                    user.roles.map((role: string) => (
                      <span
                        key={role}
                        className="px-2 py-0.5 rounded text-[0.6875rem] font-medium bg-primary/10 text-primary"
                      >
                        {getRoleLabel(role)}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-text-muted">No system roles</span>
                  )}
                </div>
              </div>

              {/* Custom roles */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-text-muted uppercase tracking-wider">
                  <KeyRound size={13} />
                  Custom Roles
                </div>
                <div className="flex flex-wrap gap-1">
                  {user.customRoles?.length ? (
                    user.customRoles.map((role: any) => (
                      <span
                        key={role.id}
                        className="px-2 py-0.5 rounded text-[0.6875rem] font-medium bg-secondary/10 text-secondary border border-border"
                      >
                        {role.name}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-text-muted">No custom roles</span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer actions */}
        {user && (
          <div className="flex flex-wrap items-center justify-end gap-3 px-4 py-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
            >
              Close
            </button>
            {canUpdate && (
              <button
                type="button"
                onClick={() => setShowUsernameModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 text-secondary rounded-lg hover:bg-secondary/20 transition-colors text-sm font-medium"
              >
                <AtSign size={16} />
                Change Username
              </button>
            )}
            {canUpdate && (
              <button
                type="button"
                onClick={() => setShowPasswordModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 text-secondary rounded-lg hover:bg-secondary/20 transition-colors text-sm font-medium"
              >
                <Lock size={16} />
                Change Password
              </button>
            )}
            {onManageRoles && (
              <button
                type="button"
                onClick={() => onManageRoles(user)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 text-secondary rounded-lg hover:bg-secondary/20 transition-colors text-sm font-medium"
              >
                <KeyRound size={16} />
                Manage Roles
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(user)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
              >
                <Pencil size={16} />
                Edit User
              </button>
            )}
          </div>
        )}
      </div>
    </div>

    {showUsernameModal && user && (
      <ChangeUsernameModal user={user} onClose={() => setShowUsernameModal(false)} />
    )}
    {showPasswordModal && user && (
      <ChangePasswordModal user={user} onClose={() => setShowPasswordModal(false)} />
    )}
    </>
  );
}
